import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth, useBlogPosts } from '@/hooks/useBlog';
import BlogPostForm from '@/components/BlogPostForm';
import BlogPostList from '@/components/BlogPostList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBlogPage = () => {
  const { checkAuth, getAuthHeaders, logout } = useAuth();
  const { blogPosts, fetchBlogPosts } = useBlogPosts();
  
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    excerpt: '',
    category: '',
    content: '',
    image_url: '',
    published: true
  });
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    checkAuth();
    fetchBlogPosts();
  }, [checkAuth, fetchBlogPosts]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataObj = new FormData();
    formDataObj.append('file', file);

    setUploading(true);
    try {
      const response = await axios.post(`${API}/upload`, formDataObj, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      setImagePreview(imageUrl);
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        await axios.put(`${API}/blog/posts/${editingPost.id}`, formData, {
          headers: getAuthHeaders()
        });
      } else {
        await axios.post(`${API}/blog/posts`, formData, {
          headers: getAuthHeaders()
        });
      }
      resetForm();
      fetchBlogPosts();
    } catch (error) {
      console.error('Failed to save blog post:', error);
      alert('Failed to save blog post. Please try again.');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      date: post.date,
      excerpt: post.excerpt,
      category: post.category,
      content: post.content || '',
      image_url: post.image_url || '',
      published: post.published
    });
    setImagePreview(post.image_url || '');
    setShowForm(true);
  };

  const handleDelete = async (postId) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      try {
        await axios.delete(`${API}/blog/posts/${postId}`, {
          headers: getAuthHeaders()
        });
        fetchBlogPosts();
      } catch (error) {
        console.error('Failed to delete blog post:', error);
        alert('Failed to delete blog post. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingPost(null);
    setFormData({
      title: '',
      date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
      excerpt: '',
      category: '',
      content: '',
      image_url: '',
      published: true
    });
    setImagePreview('');
  };

  const toggleForm = () => {
    if (showForm) {
      resetForm();
    } else {
      setShowForm(true);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-blog-page">
      <div className="content-container section-spacing">
        <div className="flex justify-between items-center mb-8">
          <h1 className="font-rubik text-3xl font-bold text-[#aaae10]">BLOG MANAGEMENT</h1>
          <div className="flex gap-2">
            <Link to="/blog" className="btn-secondary">View Blog</Link>
            <button onClick={logout} className="btn-secondary">Logout</button>
            <button
              onClick={toggleForm}
              className="btn-primary"
              data-testid="add-new-post-button"
            >
              {showForm ? 'Cancel' : 'Add New Post'}
            </button>
          </div>
        </div>

        {showForm && (
          <BlogPostForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            editingPost={editingPost}
            uploading={uploading}
            imagePreview={imagePreview}
            onImageUpload={handleImageUpload}
          />
        )}

        <BlogPostList
          blogPosts={blogPosts}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
};

export default AdminBlogPage;
